import type { ActionOffer } from '@/services/my-work/my-work.types'
import type { FleetLoop, FleetStartRunResult } from './claude-fleet.types'
import { activityFromLoopName } from './claude-fleet.utils'

export interface FleetStartRunTemplate {
  command: string
  description: string
}

/** A `<placeholder>` in a command template, which the admin replaces before the run can start. */
const PLACEHOLDER = /<[A-Za-z][\w-]*>/g

/**
 * What the Start a fleet run dialog fills in for each activity the fleet has a runner pool for.
 * A scheduled activity's command comes from its loop (the saved fleet settings), so these commands
 * are only its fallback when the loops have not loaded. The PR commands are the ones the fleet's
 * PR watcher queues for one PR.
 */
const ACTIVITY_TEMPLATES: Record<string, FleetStartRunTemplate> = {
  'backlog-triage': {
    command: '/agents:backlog-triage --limit=5 --yes --unattended',
    description:
      'Checks the oldest TE tickets for whether they are still valid and records a verdict on each.'
  },
  'lpe-loop': {
    command: '/agents:lpe-loop --lane=9 --difficulty=easy',
    description:
      "One delivery lane tick: answers review feedback on the lane's PRs, then delivers one ticket."
  },
  'fixme-sweep': {
    command: '/agents:fixme-sweep --heal --max-heals=6 --ship --yes',
    description:
      "Re-runs last night's failing and quarantined Playwright tests, opens draft PRs for the quarantine changes and test fixes, and posts a #dev-qa summary. Posts nothing when nothing changed."
  },
  'lane-size': {
    command: '/agents:lane-size --per-day=10 --max=15 --yes',
    description:
      'Labels new tickets in the delivery queue, and up to the day\'s number of older ones, as easy, medium or hard, and fills in severity, reach and location count where they are still Jira\'s default.'
  },
  cover: {
    command: '/agents:cover --ship --max=3 --yes',
    description:
      'Writes the missing tests for the three most severe open coverage gaps and opens draft pull requests for the ones that pass the test-only check.'
  },
  'qa-fields-audit': {
    command: '/agents:qa-fields-audit --unattended',
    description:
      'Fills missing QA Assignee and QA Failure Reasons and corrects inflated QA Failure Counts on the current and previous releases\' tickets.'
  },
  'mention-sweep': {
    command: '/agents:mention-sweep --yes',
    description: 'Answers @Claude mentions in Slack, Jira and Bitbucket that have no reply yet.'
  },
  'pr-fix': {
    command: '/agents:fix-queue --only=<repo>#<pr-id> --yes',
    description:
      'Fixes the confirmed review findings on one PR, pushes, and replies on each thread.'
  },
  'pr-review': {
    command: '/agents:review-queue --only=<repo>#<pr-id> --force-only --yes',
    description: 'Reviews one PR and posts the review, even if it was reviewed before.'
  },
  'qa-verify': {
    command: '/agents:qa-verify <TE-key> --yes',
    description:
      'Tests one ticket on dev or staging once its fix is deployed, comments the result, and moves a passing ticket to Done or Done in Staging. Add --env=dev or --env=staging to pick one.'
  }
}

export const FLEET_TEMPLATE_ACTIVITIES = Object.keys(ACTIVITY_TEMPLATES)

/** The activities someone who is not a fleet operator may start: one PR reviewed or fixed, or one ticket QA-tested (the fleet refuses the rest). */
export const PERSON_STARTABLE_ACTIVITIES = ['pr-fix', 'pr-review', 'qa-verify']

/** A run's Run again, in its drawer and in the Runs table's menu: an operator repeats any activity, anyone else only one they could start. */
export function canRunAgain(activity: string, rights: { canStartRun: boolean; canAct: boolean }): boolean {
  return rights.canStartRun && (rights.canAct || PERSON_STARTABLE_ACTIVITIES.includes(activity))
}

/**
 * The command and description to start `activity` with: the command its scheduled loop runs when
 * there is one, else the activity's template. Null for an activity the dialog knows nothing about.
 */
export function startRunTemplateFor(
  activity: string | null,
  loops: FleetLoop[]
): FleetStartRunTemplate | null {
  if (!activity) return null
  const template = ACTIVITY_TEMPLATES[activity]
  const loopCommand = loops
    .find(loop => activityFromLoopName(loop.name) === activity)
    ?.command.trim()
  if (loopCommand?.startsWith('/agents:')) {
    return {
      command: loopCommand,
      description: template?.description ?? `Runs what the ${activity} loop runs on its schedule.`
    }
  }
  return template ?? null
}

/** The `<placeholder>`s still left in a command, in order and without repeats. */
export function placeholdersIn(command: string): string[] {
  return [...new Set(command.match(PLACEHOLDER) ?? [])]
}

/** The Activity choice that delivers one ticket through My work's Deliver instead of starting a command. */
export const DELIVER_ACTIVITY = 'deliver-ticket'

/** My work's Deliver offer, which the dialog starts for the ticket typed in. */
export const DELIVER_OFFER: ActionOffer = {
  id: 'ticket_deliver',
  action: 'ticket_deliver',
  label: 'Deliver',
  available: true,
  confirm: 'Claude researches, builds, tests and opens a PR for this ticket.'
}

const TICKET_KEY = /^TE-\d{1,7}$/

/** The ticket typed in as a key (`te-123` → `TE-123`); null when it is not a TE ticket. */
export function deliverTicketKey(text: string): string | null {
  const key = text.trim().toUpperCase()
  return TICKET_KEY.test(key) ? key : null
}

/** The outcome a start the fleet refused with its own sentence (400) is returned as, that sentence its detail. */
export const RUN_REFUSED = 'refused'

/** Whether the fleet started or queued the run. */
export function isRunStarted(result: FleetStartRunResult): boolean {
  return /^(started|ok|queued)/i.test(result.outcome)
}

/** What the toast says: a refusal's own sentence, else the outcome with its detail. */
export function startRunResultText(result: FleetStartRunResult): string {
  if (result.outcome === RUN_REFUSED && result.detail) return result.detail
  return result.detail ? `${result.outcome}: ${result.detail}` : result.outcome
}
